import { useState } from 'react';
import { useLessons, useLesson } from '@/hooks/useProcessing';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronLeft, Calendar, ExternalLink } from 'lucide-react';
import { formatDate } from '@/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DailyLesson } from '@/types';

function LessonList({ onSelect }: { onSelect: (lesson: DailyLesson) => void }) {
  const { data: lessons, isLoading } = useLessons(10);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!lessons || lessons.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">暂无研究课程</p>
          <p className="text-sm text-muted-foreground mt-2">
            等待定时任务生成或手动触发处理
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {lessons.map((lesson: DailyLesson) => (
        <Card
          key={lesson.id}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => onSelect(lesson)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg mb-2">{lesson.topic}</CardTitle>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {lesson.tldr}
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground ml-4">
                <Calendar className="h-4 w-4" />
                {formatDate(lesson.created_at)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge
                variant={lesson.evidence_quality === 'high' ? 'default' : 'secondary'}
              >
                证据质量: {lesson.evidence_quality === 'high' ? '高' : lesson.evidence_quality === 'medium' ? '中' : '低'}
              </Badge>
              <span className="text-sm text-muted-foreground ml-auto">
                {lesson.references?.length || 0} 个参考来源
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LessonDetail({ lesson, onBack }: { lesson: DailyLesson; onBack: () => void }) {
  const { data: fullLesson, isLoading } = useLesson(lesson.id);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const displayLesson = fullLesson || lesson;
  
  return (
    <div>
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ChevronLeft className="h-4 w-4 mr-2" />
        返回列表
      </Button>
      
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl mb-2">{displayLesson.topic}</CardTitle>
              <p className="text-muted-foreground">{displayLesson.tldr}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground ml-4">
              <Calendar className="h-4 w-4" />
              {formatDate(displayLesson.created_at)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Background */}
          <div>
            <h3 className="text-lg font-semibold mb-3">背景与概念</h3>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayLesson.background}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* What Happened */}
          <div>
            <h3 className="text-lg font-semibold mb-3">发生了什么</h3>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayLesson.what_happened}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* Perspectives */}
          {displayLesson.perspectives && displayLesson.perspectives.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">多元观点</h3>
              <div className="space-y-4">
                {displayLesson.perspectives.map((perspective: {view: string, content: string}, index: number) => (
                  <div key={index} className="p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2">{perspective.view}</h4>
                    <p className="text-muted-foreground">{perspective.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Key Controversy */}
          <div>
            <h3 className="text-lg font-semibold mb-3">核心争议</h3>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayLesson.key_controversy}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* Thinking Framework */}
          <div>
            <h3 className="text-lg font-semibold mb-3">思考框架</h3>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayLesson.thinking_framework}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* Insights */}
          <div>
            <h3 className="text-lg font-semibold mb-3">洞察与启发</h3>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayLesson.insights}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* Exploration Questions */}
          {displayLesson.exploration_questions && displayLesson.exploration_questions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">深入探索问题</h3>
              <ul className="list-decimal pl-6 space-y-2">
                {displayLesson.exploration_questions.map((question: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{question}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Key Signals */}
          {displayLesson.key_signals && displayLesson.key_signals.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">关注信号</h3>
              <ul className="list-disc pl-6 space-y-1">
                {displayLesson.key_signals.map((signal: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{signal}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* References */}
          {displayLesson.references && displayLesson.references.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">参考来源</h3>
              <div className="space-y-2">
                {displayLesson.references.map((ref: {url: string, title: string, type?: string}, index: number) => (
                  <a
                    key={index}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{ref.title}</p>
                      <p className="text-sm text-muted-foreground truncate">{ref.url}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {ref.type === 'tavily' ? '搜索' : ref.type === 'llm' ? 'AI' : 'X'}
                    </Badge>
                  </a>
                ))}
              </div>
            </div>
          )}
          
          {/* Footer */}
          <div className="flex items-center gap-2 pt-6 border-t border-border">
            <Badge
              variant={displayLesson.evidence_quality === 'high' ? 'default' : 'secondary'}
            >
              证据质量: {displayLesson.evidence_quality === 'high' ? '高' : displayLesson.evidence_quality === 'medium' ? '中' : '低'}
            </Badge>
            <span className="text-sm text-muted-foreground ml-auto">
              {displayLesson.references?.length || 0} 个参考来源
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LessonsPage() {
  const [selectedLesson, setSelectedLesson] = useState<DailyLesson | null>(null);
  
  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">深度研究</h2>
      
      {selectedLesson ? (
        <LessonDetail
          lesson={selectedLesson}
          onBack={() => setSelectedLesson(null)}
        />
      ) : (
        <LessonList onSelect={setSelectedLesson} />
      )}
    </div>
  );
}
